///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Danger Zone card on the Account tab: permanent, self-service account
/// deletion (App Store 5.1.1(v) / Play Data safety / GDPR Art. 17). The
/// server cancels any Stripe subscription, removes stored files and
/// cascades the user row; the mutation signs this device out locally.
///

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { toast } from "@/components/ui/use-toast"
import { setProfileOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useDeleteAccountMutation } from "@/redux/userApi"
import { track } from "@/utils/analytics"
import { Loader2, Trash2Icon, TriangleAlertIcon } from "lucide-react"
import { useState } from "react"
import { SectionHeader } from "./SectionHeader"

const cConfirmWord = "DELETE"

export function DeleteAccountCard() {
	const dispatch = useAppDispatch()
	const userProfile = useAppSelector((state) => state.user.userProfile)
	const [deleteAccount, { isLoading }] = useDeleteAccountMutation()
	const [open, setOpen] = useState(false)
	const [confirmText, setConfirmText] = useState("")

	const confirmed = confirmText.trim().toUpperCase() === cConfirmWord

	const handleDelete = async () => {
		try {
			await deleteAccount().unwrap()
			track("account_deleted", {})
			setOpen(false)
			dispatch(setProfileOpen(false))
			toast({
				title: "Account deleted",
				description: "Your account and data have been removed.",
			})
		} catch (error) {
			const message = (error as { data?: { message?: string } })?.data?.message
			toast({
				title: "Could not delete account",
				description: message ?? "Please try again or contact support.",
				variant: "destructive",
			})
		}
	}

	return (
		<div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
			<SectionHeader
				icon={TriangleAlertIcon}
				title="Danger Zone"
				description="These actions cannot be undone"
				color="red"
			/>
			<p className="text-xs text-muted-foreground mb-3">
				Deleting your account permanently removes your profile, saved views, placemarks and alerts, stories,
				uploaded datasets, share links, devices and passkeys, and cancels any active subscription.
			</p>
			<Button
				variant="outline"
				className="w-full rounded-lg border-red-500/50 text-red-600 hover:bg-red-500/10 hover:border-red-500"
				onClick={() => { setConfirmText(""); setOpen(true) }}
			>
				<Trash2Icon className="h-4 w-4 mr-2" />
				Delete Account
			</Button>

			<AlertDialog open={open} onOpenChange={(next) => { if (!isLoading) setOpen(next) }}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete your account?</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-3">
								<p>
									This permanently deletes <span className="font-medium text-foreground">{userProfile?.email}</span> and
									everything saved to it. Any active subscription is cancelled immediately.
									There is no way to recover a deleted account.
								</p>
								<p>Type <span className="font-mono font-semibold text-foreground">{cConfirmWord}</span> to confirm.</p>
								<Input
									value={confirmText}
									onChange={(event) => setConfirmText(event.target.value)}
									placeholder={cConfirmWord}
									autoComplete="off"
									autoCapitalize="characters"
									disabled={isLoading}
									aria-label={`Type ${cConfirmWord} to confirm`}
								/>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className="bg-red-600 text-white hover:bg-red-700"
							disabled={!confirmed || isLoading}
							onClick={(event) => { event.preventDefault(); void handleDelete() }}
						>
							{isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2Icon className="h-4 w-4 mr-2" />}
							{isLoading ? "Deleting…" : "Delete permanently"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
